import React, { useEffect, useRef, useState } from 'react'
import { IconEllipses } from '../../icons/library'

/**
 * A component that truncates text to a specified number of lines.
 * It shows an ellipsis icon when the text is truncated and allows the user to expand it.
 * @param lines - The number of lines to display before truncating. Default is 4.
 * @param opened - Whether the text is initially opened or closed. Default is false (closed).
 * @param children - The text content to display.
 * @param className - Additional classes for styling the text container.
 */
export const TruncatedText: React.FC<{
  lines?: number
  opened?: boolean
  children: React.ReactNode
  className?: string
}> = ({ lines = 4, opened = false, children, className }) => {
  const [manuallyOpened, setManuallyOpened] = useState(opened)
  const [maxHeight, setMaxHeight] = useState<string>('0px')
  const textRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const text = textRef.current
    if (!text) return

    const lineHeight = parseFloat(getComputedStyle(text).lineHeight)
    const maxHeightCutoff = lineHeight * lines
    const maxHeightVisible = maxHeightCutoff - lineHeight

    const resizeObserver = new ResizeObserver(() => {
      setMaxHeight(text.scrollHeight <= maxHeightCutoff ? 'none' : `${maxHeightVisible}px`)
    })
    resizeObserver.observe(text)

    setMaxHeight(text.scrollHeight <= maxHeightCutoff ? 'none' : `${maxHeightVisible}px`)

    return () => resizeObserver.disconnect()
  }, [lines])

  const showAll = maxHeight === 'none' || manuallyOpened

  return (
    <div className="relative">
      <div
        ref={textRef}
        className={`overflow-hidden ${className || ''}`}
        style={{
          maxHeight: showAll ? 'none' : maxHeight,
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
      {!showAll && (
        <div
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation()
            setManuallyOpened(true)
          }}
        >
          <IconEllipses />
        </div>
      )}
    </div>
  )
}
